// @flow
import type { UseFormReturn } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { notifyDanger } from '@gandi/notifications';
import {
  contactbook,
  type FavoriteContact,
} from '@gandi/redux-resource-contactbook';
import { useTranslator } from '@gandi/translate';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { useHandleFormErrorSubmit } from '../../../ContactForm/hooks/useHandleFormError';
import type { ServerErrorResponse } from '../../../ContactForm/hooks/useHandleFormError';
import { saveDraftContact } from '../../modules/actions';
import { getSetupOverviewUrl } from '../../modules/helpers';
import { selectSetupOwner } from '../../modules/selectors';

import type { CreateOrTransfer, EditableRole } from '../../types';
import { contactValidation } from '../modules/actions';

import type { ContactMode, FormValues } from './types';

function sanitizeContactByType(contact: FavoriteContact) {
  if (contact.type === 'individual') {
    const { orgname, ...rest } = contact;
    return rest;
  }
  return contact;
}

export function useContactSubmitHandlers(
  form: UseFormReturn<FormValues>,
  mode: ContactMode,
): (data: FormValues) => Promise<void> {
  const { __ } = useTranslator();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const orgId = useAppSelector(selectSetupOwner);
  const formErrorCallback = useHandleFormErrorSubmit(form);

  const { cartId, process, role } = useParams<{
    cartId: string,
    process: CreateOrTransfer,
    role: EditableRole,
  }>();

  async function notifyAndHandleError(
    err: ServerErrorResponse,
    onError: ServerErrorResponse => void,
  ) {
    dispatch(notifyDanger({ message: __('Verify the information') }));
    onError(err);
  }

  function finalizeSubmit(contact: FavoriteContact) {
    dispatch(saveDraftContact({ process, contact, role }));
    navigate(getSetupOverviewUrl(cartId, process));
  }

  async function onSubmitEditContact(data: FormValues) {
    const { saveToContactbook, extra_parameters, ...contact } = data;
    const contactSanityzed = sanitizeContactByType(contact);

    if (saveToContactbook) {
      try {
        if (contact.id) {
          await dispatch(
            contactbook.editContact(
              orgId,
              contactSanityzed.id,
              contactSanityzed,
            ),
          );
        } else {
          const response = await dispatch(
            contactbook.createContact(orgId, contactSanityzed),
          );
          // $FlowFixMe[incompatible-use]
          // $FlowFixMe[incompatible-type]
          // $FlowFixMe[prop-missing]
          contactSanityzed.id = response.data.id;
        }
      } catch (err) {
        await notifyAndHandleError(err, formErrorCallback);
        return;
      }
      dispatch(contactbook.invalidate());
    } else {
      contactSanityzed.id = '';
      try {
        await dispatch(contactValidation({ contact: contactSanityzed, role }));
      } catch (err) {
        await notifyAndHandleError(err, e =>
          // $FlowFixMe[prop-missing]
          formErrorCallback({ response: e }),
        );
        return;
      }
    }

    finalizeSubmit(contactSanityzed);
  }

  async function onSubmitCreateContact(data: FormValues) {
    const { extra_parameters, saveToContactbook, ...contact } = data;
    const contactSanityzed = sanitizeContactByType(contact);

    if (saveToContactbook) {
      try {
        const response = await dispatch(
          contactbook.createContact(orgId, contactSanityzed),
        );
        // $FlowFixMe[incompatible-use]
        // $FlowFixMe[incompatible-type]
        // $FlowFixMe[prop-missing]
        contactSanityzed.id = response.data.id;
      } catch (err) {
        await notifyAndHandleError(err, formErrorCallback);
        return;
      }
      dispatch(contactbook.invalidate());
    } else {
      try {
        await dispatch(contactValidation({ contact: contactSanityzed, role }));
      } catch (err) {
        await notifyAndHandleError(err, e =>
          // $FlowFixMe[prop-missing]
          formErrorCallback({ response: e }),
        );
        return;
      }
    }

    finalizeSubmit(contactSanityzed);
  }

  return mode === 'EditContact' ? onSubmitEditContact : onSubmitCreateContact;
}
